import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { initializePayment } from "../services/paymentService";
import { useState } from "react";

const Pricing = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingPlanCode, setPendingPlanCode] = useState("");

  const handleUpgrade = async (planCode: string) => {
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }

    const alreadyPaid = user?.isPaid === true || (user as any)?.paid === true;

    if (alreadyPaid) {
      alert(
        "⚠️ You already have an active subscription! You cannot subscribe to multiple plans. Please contact muoghaluijeoma9@gmail.com if you want to change your plan.",
      );
      return;
    }

    setPendingPlanCode(planCode);
    setAgreedToTerms(false);
    setShowTermsModal(true);
  };

  const handleConfirmUpgrade = async () => {
    setShowTermsModal(false);
    setLoading(pendingPlanCode);
    try {
      const response = await initializePayment(pendingPlanCode, billingCycle);
      window.location.href = response.authorizationUrl;
    } catch (err: any) {
      alert(err.response?.data?.message || "Payment failed!");
    } finally {
      setLoading("");
    }
  };

  const isCurrentPlan = (planCode: string) => {
    if (planCode === "FREE" && user?.plan === "FREE") return true;
    if (planCode === "PRO" && user?.plan === "PRO") return true;
    if (planCode === "AGENCY" && user?.plan === "AGENCY") return true;
    return false;
  };

  const plans = [
    {
      category: "Basic",
      plans: [
        {
          name: "FREE",
          planCode: "FREE",
          monthlyPrice: "$0",
          yearlyPrice: "$0",
          period: "forever",
          color: "border-gray-700",
          buttonColor: "bg-gray-700 hover:bg-gray-600",
          description: "Perfect for getting started",
          features: [
            "4 scripts per month",
            "All platforms supported",
            "Full script with HOOK & OUTRO",
            "Title + Description",
            "Hashtags included",
            "Thumbnail ideas",
            "Script history",
            "5 or 10 minute scripts",
            "Series with story memory",
            "Character looks & voices",
            "Shot list generator",
            "Edit scripts & shot lists",
            "Character reference prompts",
          ],
          notIncluded: [
            "Shot list PDF download",
            "Character reference images & AI thumbnails",
            "AI video shots",
            "AI voiceovers (MP3)",
            "Unlimited scripts",
            "Custom tone & style",
            "Multiple languages",
            "Team members",
          ],
        },
        {
          name: "PRO",
          planCode: "PRO",
          monthlyPrice: "$5",
          yearlyPrice: "$50",
          period: "per month",
          color: "border-purple-500",
          buttonColor: "bg-purple-600 hover:bg-purple-700",
          popular: true,
          description: "For serious content creators",
          features: [
            "10 scripts per month",
            "All platforms supported",
            "Full script with HOOK & OUTRO",
            "Title + Description",
            "Hashtags included",
            "Thumbnail ideas",
            "Script history",
            "5 or 10 minute scripts",
            "Series with story memory",
            "Character looks & voices",
            "Shot list generator",
            "Edit scripts & shot lists",
            "Character reference prompts",
            "Shot list PDF download",
            "Character reference images & AI thumbnails (10 per week)",
            "AI video shots (with video credits)",
          ],
          notIncluded: [
            "AI voiceovers (MP3)",
            "Custom tone & style",
            "Multiple languages",
            "Team members",
          ],
        },
      ],
    },
    {
      category: "Agency",
      plans: [
        {
          name: "AGENCY Individual",
          planCode: "AGENCY",
          monthlyPrice: "$15",
          yearlyPrice: "$150",
          period: "per month",
          color: "border-yellow-500",
          buttonColor: "bg-yellow-500 hover:bg-yellow-600 text-black",
          description: "All Agency features for one person",
          features: [
            "Unlimited scripts",
            "All platforms supported",
            "Full script with HOOK & OUTRO",
            "Title + Description",
            "Hashtags included",
            "Thumbnail ideas",
            "Script history",
            "5 or 10 minute scripts",
            "Series with story memory",
            "Character looks & voices",
            "Shot list generator",
            "Edit scripts & shot lists",
            "Character reference prompts",
            "Shot list PDF download",
            "Character reference images & AI thumbnails (30 per week)",
            "AI video shots (with video credits)",
            "5 free video clips every month",
            "AI voiceovers (MP3)",
            "Custom tone & style",
            "Multiple languages",
            "Dedicated support",
          ],
          notIncluded: ["Team members"],
        },
        {
          name: "AGENCY Team",
          planCode: "AGENCY_TEAM",
          monthlyPrice: "$30",
          yearlyPrice: "$300",
          period: "per month",
          color: "border-yellow-500",
          buttonColor: "bg-yellow-500 hover:bg-yellow-600 text-black",
          description: "Agency features + up to 10 team members",
          features: [
            "Unlimited scripts",
            "All platforms supported",
            "Full script with HOOK & OUTRO",
            "Title + Description",
            "Hashtags included",
            "Thumbnail ideas",
            "Script history",
            "5 or 10 minute scripts",
            "Series with story memory",
            "Character looks & voices",
            "Shot list generator",
            "Edit scripts & shot lists",
            "Character reference prompts",
            "Shot list PDF download",
            "Character reference images & AI thumbnails (30 per week)",
            "AI video shots (with video credits)",
            "5 free video clips every month",
            "AI voiceovers (MP3)",
            "Custom tone & style",
            "Multiple languages",
            "Dedicated support",
            "Up to 10 team members",
          ],
          notIncluded: [],
        },
        {
          name: "AGENCY Unlimited",
          planCode: "AGENCY_UNLIMITED",
          monthlyPrice: "$50",
          yearlyPrice: "$500",
          period: "per month",
          color: "border-green-500",
          buttonColor: "bg-green-600 hover:bg-green-700",
          description: "Agency features + unlimited team members",
          features: [
            "Unlimited scripts",
            "All platforms supported",
            "Full script with HOOK & OUTRO",
            "Title + Description",
            "Hashtags included",
            "Thumbnail ideas",
            "Script history",
            "5 or 10 minute scripts",
            "Series with story memory",
            "Character looks & voices",
            "Shot list generator",
            "Edit scripts & shot lists",
            "Character reference prompts",
            "Shot list PDF download",
            "Character reference images & AI thumbnails (30 per week)",
            "AI video shots (with video credits)",
            "5 free video clips every month",
            "AI voiceovers (MP3)",
            "Custom tone & style",
            "Multiple languages",
            "Dedicated support",
            "Unlimited team members",
          ],
          notIncluded: [],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-16">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Simple,{" "}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Transparent
            </span>{" "}
            Pricing
          </h1>
          <p className="text-gray-400 text-base md:text-lg">
            Start free, upgrade when you're ready!
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`w-full sm:w-auto px-6 py-2 rounded-xl font-semibold transition ${
              billingCycle === "monthly"
                ? "bg-purple-600 text-white"
                : "bg-gray-800 text-gray-400 hover:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`w-full sm:w-auto px-6 py-2 rounded-xl font-semibold transition ${
              billingCycle === "yearly"
                ? "bg-purple-600 text-white"
                : "bg-gray-800 text-gray-400 hover:text-white"
            }`}
          >
            Yearly
            <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
              Save 2 months!
            </span>
          </button>
        </div>

        {/* Plans */}
        {plans.map((category) => (
          <div key={category.category} className="mb-12">
            <h2 className="text-xl font-bold text-gray-400 mb-6">
              {category.category} Plans
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.plans.map((plan) => (
                <div
                  key={plan.planCode}
                  className={`bg-gray-800 rounded-2xl p-6 md:p-8 border-2 ${plan.color} relative`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-purple-600 text-white text-xs px-4 py-1 rounded-full font-semibold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  <div className="mb-2">
                    <h2 className="text-xl font-bold">{plan.name}</h2>
                    <p className="text-gray-400 text-sm mt-1">
                      {plan.description}
                    </p>
                  </div>

                  <div className="flex items-end gap-1 mb-6">
                    <span className="text-4xl font-bold">
                      {billingCycle === "yearly"
                        ? plan.yearlyPrice
                        : plan.monthlyPrice}
                    </span>
                    <span className="text-gray-400 mb-1">
                      /{billingCycle === "yearly" ? "year" : "month"}
                    </span>
                  </div>

                  {billingCycle === "yearly" && plan.planCode !== "FREE" && (
                    <p className="text-green-400 text-xs mb-4">
                      2 months free!
                    </p>
                  )}

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="text-green-400">✅</span>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                    {plan.notIncluded.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span className="text-gray-600">❌</span>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan(plan.planCode) ? (
                    <button
                      disabled
                      className="w-full bg-gray-700 text-gray-400 py-3 rounded-xl font-semibold cursor-not-allowed"
                    >
                      Current Plan ✓
                    </button>
                  ) : plan.planCode === "FREE" ? (
                    <button
                      onClick={() => navigate("/register")}
                      className={`w-full ${plan.buttonColor} text-white py-3 rounded-xl font-semibold transition`}
                    >
                      Get Started Free
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan.planCode)}
                      disabled={loading === plan.planCode}
                      className={`w-full ${plan.buttonColor} py-3 rounded-xl font-semibold transition disabled:opacity-50`}
                    >
                      {loading === plan.planCode
                        ? "Processing..."
                        : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Video credits note */}
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6 mb-8 text-center">
          <h3 className="text-lg font-bold mb-2">AI video shots</h3>
          <p className="text-gray-400 text-sm">
            PRO and Agency users can turn any shot into a video clip using video
            credits (1 credit = 1 clip). Credit packs: 5 credits for ₦10,000, 10
            credits for ₦19,000, or 25 credits for ₦45,000. Credits never
            expire, and if a clip fails, the credit is returned.
          </p>
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-400 text-sm md:text-base">
            Questions? Email us at{" "}
            <a
              href="mailto:muoghaluijeoma9@gmail.com"
              className="text-purple-400 hover:text-purple-300"
            >
              muoghaluijeoma9@gmail.com
            </a>
          </p>
        </div>
      </div>
      {showTermsModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-60 z-50"
            onClick={() => setShowTermsModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4">Terms & Conditions</h2>
              <p className="text-gray-400 text-xs mb-4">
                Please read and agree before proceeding with payment.
              </p>

              <div className="bg-gray-900 rounded-xl p-4 text-sm text-gray-300 space-y-3 mb-6 max-h-60 overflow-y-auto">
                <p>
                  <strong className="text-white">
                    1. Non-Refundable Subscription
                  </strong>
                  <br />
                  All payments made to TubeScript AI are non-refundable. Once a
                  subscription is activated, no refunds will be issued
                  regardless of usage.
                </p>

                <p>
                  <strong className="text-white">2. Account Usage</strong>
                  <br />
                  Your account is for personal use only. Sharing account
                  credentials with others is strictly prohibited and may result
                  in account suspension.
                </p>

                <p>
                  <strong className="text-white">3. Content Ownership</strong>
                  <br />
                  All scripts generated using TubeScript AI are owned by you. We
                  do not claim any rights over content you create using our
                  platform.
                </p>

                <p>
                  <strong className="text-white">
                    4. Service Availability
                  </strong>
                  <br />
                  We strive to maintain 99% uptime but do not guarantee
                  uninterrupted access to the service. Scheduled maintenance may
                  occur.
                </p>

                <p>
                  <strong className="text-white">5. Account Suspension</strong>
                  <br />
                  TubeScript AI reserves the right to suspend or terminate
                  accounts that abuse the platform, violate these terms, or
                  engage in fraudulent activity.
                </p>

                <p>
                  <strong className="text-white">
                    6. Subscription Renewal
                  </strong>
                  <br />
                  Subscriptions renew automatically unless cancelled before the
                  renewal date. You are responsible for managing your
                  subscription.
                </p>

                <p>
                  <strong className="text-white">
                    7. One Account Per User
                  </strong>
                  <br />
                  Each user is permitted one account only. Creating multiple
                  accounts to exploit free trials or promotions is strictly
                  prohibited and will result in immediate suspension of all
                  associated accounts.
                </p>

                <p>
                  <strong className="text-white">8. Changes to Terms</strong>
                  <br />
                  TubeScript AI reserves the right to update these terms at any
                  time. Continued use of the service constitutes acceptance of
                  updated terms.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer mb-6">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-purple-500"
                />
                <span className="text-sm text-gray-300">
                  I have read and agree to the{" "}
                  <strong className="text-white">Terms & Conditions</strong>. I
                  understand that my subscription is{" "}
                  <strong className="text-white">non-refundable</strong>.
                </span>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="flex-1 border border-gray-600 hover:border-gray-400 text-gray-400 py-3 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpgrade}
                  disabled={!agreedToTerms}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Pricing;
